"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import type { ElementRef, ComponentPropsWithoutRef } from "react";

import { cn } from "@/utils";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("inline-flex items-center justify-center", {
  variants: {
    variant: {
      default: "h-10 rounded-md bg-muted p-1 text-muted-foreground",
      retro:
        "h-12 rounded-lg bg-gradient-to-b from-gray-800 to-gray-900 p-1 shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),inset_0_-1px_2px_rgba(255,255,255,0.02),0_1px_2px_rgba(0,0,0,0.2),0_0_0_1px_rgba(46,81,234,0.3)]",
      profile:
        "flex p-1 bg-gradient-to-br from-[#08080a] to-[#11111a] border-t border-l border-r border-[rgba(62,97,250,0.18)] rounded-t-lg shadow-[inset_0_1px_2px_rgba(0,0,0,0.6),inset_0_-1px_0_rgba(62,97,250,0.12)]",
      vault:
        "flex gap-0 p-1 rounded bg-gradient-to-br from-[#0f172a] to-[#0a0a0b] shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),0_0_0_1px_rgba(59,130,246,0.1)]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const TabsList = React.forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "rounded-sm px-3 py-1.5 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        retro:
          "rounded-md px-4 py-2.5 font-bold uppercase tracking-wider text-xs focus-visible:ring-1 focus-visible:ring-yellow-400/50 data-[state=active]:bg-gradient-to-b data-[state=active]:from-yellow-400 data-[state=active]:to-yellow-600 data-[state=active]:text-black data-[state=active]:shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_2px_rgba(255,255,255,0.35),inset_0_-1px_2px_rgba(0,0,0,0.4)] data-[state=inactive]:text-white/40 hover:data-[state=inactive]:text-white/60",
        profile:
          "flex-1 px-3 py-2 font-pressStart2P text-[8px] uppercase rounded-[7px] focus-visible:ring-1 focus-visible:ring-blue-400/30 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#1f1f25] data-[state=active]:to-[#161620] data-[state=active]:text-white data-[state=active]:border data-[state=active]:border-[rgba(62,97,250,0.22)] data-[state=active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),inset_0_-1px_0_rgba(62,97,250,0.18)] data-[state=inactive]:text-white/40 hover:data-[state=inactive]:text-white/60",
        vault:
          "relative flex-1 rounded px-4 py-3 font-pressStart2P text-xs uppercase transition-colors focus-visible:outline-none data-[state=inactive]:text-white/40 hover:data-[state=inactive]:text-white/60",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const TabsTrigger = React.forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
